'use client';

import { getDatong, useMedia } from '@ssv-website/hooks';
import { useMemo } from 'react';

/** 主站点 + 详情模板页 pgid（详情按页参区分 CMS 分组） */
export const DesignPageId = {
  Home: 'page_design_guidelines',
  Detail: 'page_design_brand_detail',
} as const;

export const DesignEid = {
  DesignGroupCard: 'design_group_card',
  HomeFooterBrandLink: 'home_footer_brand_link',
  MobileMenuBtn: 'mobile_menu_btn',
  TopbarBrandLink: 'topbar_brand_link',
  SidenavGroupBtn: 'sidenav_group_btn',
  SidenavAssetLink: 'sidenav_asset_link',
  BreadcrumbHomeLink: 'breadcrumb_home_link',
  AssetDownloadBtn: 'asset_download_btn',
  AssetExtLink: 'asset_ext_link',
} as const;

export function dtElementParams(params: Record<string, string>): string {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}

function useMediaReady(): { isMobile: boolean; ready: boolean } {
  const isMobile = useMedia('mobile') as boolean | undefined;
  return {
    ready: typeof isMobile === 'boolean',
    isMobile: Boolean(isMobile),
  };
}

export function useDesignHomeDtAttrs(): {
  'dt-pgid'?: string;
  'dt-params'?: string;
} {
  const { isMobile, ready } = useMediaReady();
  const dtParams = useMemo(
    () => (ready ? `pg_is_mobile=${isMobile ? 1 : 0}` : undefined),
    [isMobile, ready]
  );
  if (!ready) return {};
  return {
    'dt-pgid': DesignPageId.Home,
    'dt-params': dtParams,
  };
}

export function useDesignDetailDtAttrs(
  groupId: string | undefined,
  groupName: string | undefined,
  assetId: string | undefined
): {
  'dt-pgid'?: string;
  'dt-params'?: string;
} {
  const { isMobile, ready } = useMediaReady();
  const canBind =
    ready && Boolean(groupId) && Boolean(groupName) && Boolean(assetId);
  const dtParams = useMemo(() => {
    if (!canBind || !groupId || !groupName || !assetId) return undefined;
    return dtElementParams({
      pg_is_mobile: isMobile ? '1' : '0',
      pg_group_id: groupId,
      pg_group_name: groupName,
      pg_asset_id: assetId,
    });
  }, [assetId, canBind, groupId, groupName, isMobile]);
  if (!canBind) return {};
  return {
    'dt-pgid': DesignPageId.Detail,
    'dt-params': dtParams,
  };
}

/**
 * 仅用于会立刻卸页的点击（未登录下载跳登录）。
 * 声明式 dt_clck 依赖 click observer，同步赋 location.href 时 beacon 可能被掐掉。
 */
export function reportDesignClick(
  pgid: string | undefined,
  eid: string,
  businessParams?: Record<string, string | number | boolean>
) {
  if (!pgid) return;
  const reporter = getDatong();
  if (!reporter) return;
  reporter.reportEvent({
    pgid,
    eventName: 'dt_clck',
    businessParams: {
      eid,
      ...businessParams,
    },
  });
}
